<template>
  <div
    class="slide-tabs"
    :class="{'slide-tabs-eu': isEURegion, 'blur': isContentBlurred}"
    @mouseenter="handleHoverSlideTabsContent()"
    @mouseleave="handleLeaveSlideTabsContent()"
  >
    <ul class="nav nav-tabs" role="tablist">
      <li
        v-for="typeObj in allTypeSections"
        :key="typeObj.key"
        class="nav-item"
        :class="{
          'active': activeTab === typeObj.key
        }"
        role="presentation"
      >
        <button
          class="nav-link"
          type="button"
          role="tab"
          @click="setActiveTab(typeObj.key)"
        >
          {{ $i18nPresenter.t(typeObj.title) }}
        </button>
      </li>
    </ul>
    <div class="tab-content">
      <div
        v-for="typeObj in allTypeSections"
        :key="`tab-content-${typeObj.key}`"
        class="tab-pane"
        :class="{
          'active': activeTab === typeObj.key
        }"
        role="tabpanel"
        tabindex="0"
      >
        <div
          v-for="(item, index) in typeObj.slideTypes"
          :key="item.type + index + item.slideType"
          class="box-type"
          :class="{
            new: item.isNew,
            beta: item.beta,
            legacy: item.isLegacy,
            'd-none': item.hidden,
          }"
          @mouseenter="handleHoverOnSlideType(item, 'over')"
          @mouseleave="handleHoverOnSlideType(item, 'out')"
          v-show="!item.noType && !item.hidden"
        >
          <div
            class="box-content vertical layout center-justified ml-0.25 relative"
            @click="item.isLegacy ? false :
              handleSelectSlideType({ item, property: 'slide-search-popup' })"
            :class="[item.isLegacy ? 'default': 'pointer']"
          >
            <SlideTypeIcon
              :slide-type="item.type"
              :child-type="item.slideType"
              :left-side="false"
            />
            <div
              class="loading-show horizontal layout center center-justified"
              v-if="item.isLoading"
            >
              <aha-spinner />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SlideTypeIcon from '@/components-v2/common/slide-type-icon/SlideTypeIcon.vue'
import configs from '@/config'
import constants from '@/constant'
import EventBus from '@/eventBus'
import allSlideTypesLegacy from '@/constant/allSlideTypes'

export default {
  components: {
    SlideTypeIcon,
  },
  props: {
    wrapperId: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      allTypeSections: allSlideTypesLegacy,
      activeTab: 'popular',
      isContentBlurred: false,
      eventBusName: 'ai-side-left: isContentBlurred',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideGenerator: (state) => state.slideGenerator,
    }),
    isEURegion() {
      return configs.region === constants.euRegion
    },
    hasInputPrompt() {
      return this.slideGenerator.isAIPromptHasInput
    },
  },
  watch: {
    hasInputPrompt() {
      this.isContentBlurred = this.hasInputPrompt
    },
  },
  created() {
    this.getActiveTab()
  },
  mounted() {
    EventBus.$on(this.eventBusName, (value) => {
      this.isContentBlurred = value
    })
  },
  destroyed() {
    EventBus.$off(this.eventBusName)
  },
  methods: {
    getActiveTab() {
      try {
        const storeValue = localStorage.getItem('popupMenuActiveTab')
        const keys = ['popular', 'quiz', 'poll', 'content']

        if (keys.includes(storeValue)) {
          this.activeTab = storeValue
        }
      } catch {
        // do nothing
      }
    },
    setActiveTab(key) {
      this.activeTab = key
      localStorage.setItem('popupMenuActiveTab', key)
    },
    handleHoverOnSlideType(item, mode) {
      EventBus.$emit('handleHoverOnSlideType', item, mode, this.wrapperId)
    },
    handleSelectSlideType(obj) {
      this.$emit('handleSelectSlideType', obj)
    },
    handleHoverSlideTabsContent() {
      if (!this.hasInputPrompt) return
      this.isContentBlurred = false
      EventBus.$emit(this.eventBusName, false)
    },
    handleLeaveSlideTabsContent() {
      if (!this.hasInputPrompt) return
      this.isContentBlurred = true
      EventBus.$emit(this.eventBusName, true)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
.slide-tabs {
  padding: 20px;
  flex-grow: 1;
  overflow-y: auto;
  background-color: #FFFFFF;
  &.blur {
    position: relative;
    transition: 0.2s ease;
    overflow-y: hidden;
    &::after {
      content: "";
      background-color: rgba(255,255,255,0.5);
      width: 95%;
      height: 400px;
      display: block;
      position: absolute;
      z-index: 1;
      top: 0;
    }
  }
}

.nav-tabs {
  @apply flex flex-row;
  @apply border-base-30 border-b;
  font-size: 1rem;

  .nav-item {
    width: 100%;

    &.active {
      @apply text-primary-90;

      .nav-link {
        &::before {
          background-color: $primary-color;
        }
      }
    }
    &:hover {
      color: $primary-color;
    }
    .nav-link {
      @apply font-bold;
      padding: 5px 15px 20px 15px;
      width: 100%;
      position: relative;

      &::before {
        position: absolute;
        content: '';
        height: 3px;
        width: 100%;
        background-color: transparent;
        bottom: -2px;
        left: 0;
      }
    }
  }
}
.tab-content {
  @apply py-5;
}
.tab-pane {
  display: none;

  &.active {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 20px;
  }
}

.box-content {
  @apply ring-1 ring-inset ring-secondary-20;
  display: flex;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  padding: 3px 0 0;
  min-height: 80px;
  transition: 0.5s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
  &:hover {
    @apply ring-1 ring-inset ring-primary-90;
  }
  &.default {
    cursor: default;
  }
  &.pointer {
    cursor: pointer;
  }
}

.box-type {
  @apply text-secondary-50;
  width: 100%;
  text-align: center;
  position: relative;
  &.new, &.beta, &.legacy {
    .box-content {
      &:after {
        display: block;
        background: #FFB12E;
        position: absolute;
        top: 2px;
        padding-left: 20px;
        right: -21px;
        width: 80px;
        height: 16px;
        line-height: 18px;
        text-align: center;
        font-size: 8px;
        font-weight: bold;
        color: #fff;
        -webkit-transform: rotate(45deg);
        transform: rotate(45deg);
        content: 'Beta';
        text-transform: uppercase;
      }
    }
  }
  &.new {
    .box-content {
      &:after {
        @apply bg-primary-90;
        content: 'New';
        font-size: 8px;
        line-height: 16px;
        box-shadow: -1.33274px 2.66548px 5.33096px rgba(52, 142, 246, 0.31);
      }
    }
  }
  &.legacy {
    .box-content {
      &:after {
        @apply bg-secondary-50;
        content: 'Legacy';
        font-size: 7px;
        right: -20px;
        top: 0;
        line-height: 16px;
        box-shadow: -1.33274px 2.66548px 5.33096px rgba(180, 188, 207, 0.24);
      }
    }
    &::v-deep .aha-slide-type-icon {
      opacity: 0.5;
    }
  }
}

::v-deep {
  .aha-icon-slide-wrapper .text-sm {
    font-size: 1rem;
    margin-top: 0.5rem;
  }
}
</style>
